import React, { createContext, useContext, useState, useEffect } from "react";

// Create context
const CartContext = createContext();

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // State for services, packages, utility items, and appointment details
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedUtilityItems, setSelectedUtilityItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [notes, setNotes] = useState("");
  const [minimumPrice, setMinimumPrice] = useState(0);

  // Initialize state from localStorage
  useEffect(() => {
    const loadCartFromStorage = () => {
      try {
        const storedItems = localStorage.getItem("selectedServices");
        if (storedItems) {
          const parsedItems = JSON.parse(storedItems);

          // Split into services, packages, and utility items
          const services = parsedItems.filter(
            (item) => (!item.type || item.type === "service")
          );
          const pkgs = parsedItems.filter((item) => item.type === "package");
          const utilityItems = parsedItems.filter((item) => item.type === "utility");

          setSelectedServices(services);
          setPackages(pkgs);
          setSelectedUtilityItems(utilityItems);
        }

        const storedAppointment = localStorage.getItem("appointmentDetails");
        if (storedAppointment) {
          setAppointmentDetails(JSON.parse(storedAppointment));
        }

        const storedAddress = localStorage.getItem("selectedAddress");
        if (storedAddress) {
          setSelectedAddress(JSON.parse(storedAddress));
        }

        const storedPromoCode = localStorage.getItem("promoCode");
        if (storedPromoCode) {
          setPromoCode(JSON.parse(storedPromoCode));
        }

        const storedNotes = localStorage.getItem("cartNotes");
        if (storedNotes) {
          setNotes(JSON.parse(storedNotes));
        }

        const storedMinimumPrice = localStorage.getItem("cartMinimumPrice");
        if (storedMinimumPrice) {
          setMinimumPrice(parseFloat(JSON.parse(storedMinimumPrice)) || 0);
        }
      } catch (error) {
        console.error("Error loading cart from localStorage:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCartFromStorage();
  }, []);

  // Save to localStorage whenever cart state changes
  useEffect(() => {
    if (!loading) {
      // Combine services, packages, and utility items for storage
      const itemsToStore = [...selectedServices, ...packages, ...selectedUtilityItems];
      localStorage.setItem("selectedServices", JSON.stringify(itemsToStore));

      if (appointmentDetails) {
        localStorage.setItem("appointmentDetails", JSON.stringify(appointmentDetails));
      } else {
        localStorage.removeItem("appointmentDetails");
      }

      if (selectedAddress) {
        localStorage.setItem("selectedAddress", JSON.stringify(selectedAddress));
      } else {
        localStorage.removeItem("selectedAddress");
      }

      if (promoCode) {
        localStorage.setItem("promoCode", JSON.stringify(promoCode));
      } else {
        localStorage.removeItem("promoCode");
      }

      if (notes) {
        localStorage.setItem("cartNotes", JSON.stringify(notes));
      } else {
        localStorage.removeItem("cartNotes");
      }

      if (minimumPrice > 0) {
        localStorage.setItem("cartMinimumPrice", JSON.stringify(minimumPrice));
      } else {
        localStorage.removeItem("cartMinimumPrice");
      }
    }
  }, [
    selectedServices,
    selectedUtilityItems,
    packages,
    appointmentDetails,
    selectedAddress,
    promoCode,
    notes,
    minimumPrice,
    loading,
  ]);

  // Update current user and verify selected address is still valid
  useEffect(() => {
    if (currentUser && selectedAddress) {
      const addressExists =
        currentUser.address &&
        currentUser.address.some((addr) => addr._id === selectedAddress._id);

      if (!addressExists) {
        setSelectedAddress(null);
        localStorage.removeItem("selectedAddress");
      }
    }
  }, [currentUser, selectedAddress]);

  // Add service to cart
  const addService = (service) => {
    const existingServiceIndex = selectedServices.findIndex(
      (s) => s._id === service._id
    );

    if (existingServiceIndex !== -1) {
      const updatedServices = [...selectedServices];
      updatedServices[existingServiceIndex] = {
        ...updatedServices[existingServiceIndex],
        quantity: (updatedServices[existingServiceIndex].quantity || 1) + 1,
      };
      setSelectedServices(updatedServices);
    } else {
      setSelectedServices([
        ...selectedServices,
        {
          ...service,
          type: "service",
          quantity: service.quantity || 1,
        },
      ]);
    }
  };

  // Add multiple services to cart (now handles both services and utility items)
  const addServices = (
    items,
    categoryId = null,
    serviceNotes = "",
    minPrice = 0
  ) => {
    if (!items || !items.length) return;

    // Clear existing items if they are from a different category
    if (categoryId && selectedServices.length > 0) {
      const existingCategoryId = selectedServices[0].categoryId;
      if (existingCategoryId !== categoryId) {
        setSelectedServices([]);
        setSelectedUtilityItems([]);
        setAppointmentDetails(null);
      }
    }

    setNotes(serviceNotes || "");

    if (minPrice !== undefined) {
      setMinimumPrice(parseFloat(minPrice) || 0);
    }

    // Separate services and utility items
    const services = items.filter(item => !item.type || item.type === "service");
    const utilityItems = items.filter(item => item.type === "utility");

    // Handle services
    setSelectedServices((prev) => {
      const newServices = [...prev];

      services.forEach((service) => {
        const existingIndex = newServices.findIndex(
          (s) => s._id === service._id
        );

        if (existingIndex !== -1) {
          newServices[existingIndex] = {
            ...newServices[existingIndex],
            quantity: service.quantity || 1,
          };
        } else {
          newServices.push({
            ...service,
            type: "service",
            quantity: service.quantity || 1,
          });
        }
      });

      return newServices;
    });

    // Handle utility items
    setSelectedUtilityItems((prev) => {
      const newUtilityItems = [...prev];

      utilityItems.forEach((item) => {
        const existingIndex = newUtilityItems.findIndex(
          (u) => u._id === item._id
        );

        if (existingIndex !== -1) {
          newUtilityItems[existingIndex] = {
            ...newUtilityItems[existingIndex],
            quantity: item.quantity || 1,
          };
        } else {
          newUtilityItems.push({
            ...item,
            type: "utility",
            quantity: item.quantity || 1,
          });
        }
      });

      return newUtilityItems;
    });
  };

  // Add utility item to cart
  const addUtilityItem = (item) => {
    const existingItemIndex = selectedUtilityItems.findIndex(
      (u) => u._id === item._id
    );

    if (existingItemIndex !== -1) {
      const updatedItems = [...selectedUtilityItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: (updatedItems[existingItemIndex].quantity || 1) + 1,
      };
      setSelectedUtilityItems(updatedItems);
    } else {
      setSelectedUtilityItems([
        ...selectedUtilityItems,
        {
          ...item,
          type: "utility",
          quantity: item.quantity || 1,
        },
      ]);
    }
  };

  // Add package to cart
  const addPackage = (pkg) => {
    if (!packages.some((p) => p._id === pkg._id)) {
      setPackages([
        ...packages,
        {
          ...pkg,
          type: "package",
        },
      ]);
    }
  };

  // Update service quantity
  const updateServiceQuantity = (serviceId, newQuantity) => {
    if (newQuantity < 1) return;

    setSelectedServices((prev) =>
      prev.map((service) =>
        service._id === serviceId || service.id === serviceId
          ? { ...service, quantity: newQuantity }
          : service
      )
    );
  };

  // Update utility item quantity
  const updateUtilityQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setSelectedUtilityItems((prev) =>
      prev.map((item) =>
        item._id === itemId || item.id === itemId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Update minimum price
  const updateMinimumPrice = (newMinPrice) => {
    setMinimumPrice(parseFloat(newMinPrice) || 0);
  };

  // Remove item from cart (works for services, utility items, and packages)
  const removeItem = (itemId) => {
    // Check if item is a service
    if (selectedServices.some((s) => s._id === itemId || s.id === itemId)) {
      setSelectedServices(
        selectedServices.filter((s) => s._id !== itemId && s.id !== itemId)
      );
    }

    // Check if item is a utility item
    if (selectedUtilityItems.some((u) => u._id === itemId || u.id === itemId)) {
      setSelectedUtilityItems(
        selectedUtilityItems.filter((u) => u._id !== itemId && u.id !== itemId)
      );
    }

    // Check if item is a package
    if (packages.some((p) => p._id === itemId || p.id === itemId)) {
      setPackages(packages.filter((p) => p._id !== itemId && p.id !== itemId));
    }

    // If cart is empty after removal, also clear appointment, notes, and minimum price
    const remainingItems = [
      ...selectedServices.filter((s) => s._id !== itemId && s.id !== itemId),
      ...selectedUtilityItems.filter((u) => u._id !== itemId && u.id !== itemId),
      ...packages.filter((p) => p._id !== itemId && p.id !== itemId),
    ];

    if (remainingItems.length === 0) {
      setAppointmentDetails(null);
      setPromoCode(null);
      setNotes("");
      setMinimumPrice(0);
    }
  };

  // Clear entire cart
  const clearCart = () => {
    setSelectedServices([]);
    setSelectedUtilityItems([]);
    setPackages([]);
    setAppointmentDetails(null);
    setPromoCode(null);
    setNotes("");
    setMinimumPrice(0);
  };

  // Clear only services
  const clearServices = () => {
    setSelectedServices([]);
    // Clear appointment, notes, and minimum price if no packages or utility items remain
    if (packages.length === 0 && selectedUtilityItems.length === 0) {
      setAppointmentDetails(null);
      setPromoCode(null);
      setNotes("");
      setMinimumPrice(0);
    }
  };

  // Clear only utility items
  const clearUtilityItems = () => {
    setSelectedUtilityItems([]);
  };

  // Set appointment details
  const setAppointment = (details) => {
    setAppointmentDetails(details);
  };

  // Update the address data
  const updateSelectedAddress = (addressData) => {
    setSelectedAddress(addressData);
  };

  // Set promo code and discount
  const applyPromoCode = (codeData) => {
    setPromoCode(codeData);
  };

  // Remove promo code
  const removePromoCode = () => {
    setPromoCode(null);
  };

  // Update notes
  const updateNotes = (newNotes) => {
    setNotes(newNotes);
  };

  // Check if payment is allowed based on minimum price
  const isPaymentAllowed = () => {
    // For packages, no minimum price restriction
    if (packages.length > 0 && selectedServices.length === 0) {
      return true;
    }

    // For services, check minimum price (utility items don't count toward minimum)
    if (selectedServices.length > 0) {
      const servicesPrice = selectedServices.reduce(
        (sum, service) => sum + service.price * (service.quantity || 1),
        0
      );
      
      // Utility items can only be ordered with services
      if (selectedUtilityItems.length > 0 && selectedServices.length === 0) {
        return false;
      }
      
      return minimumPrice === 0 || servicesPrice >= minimumPrice;
    }

    // Cannot order only utility items
    if (selectedUtilityItems.length > 0 && selectedServices.length === 0) {
      return false;
    }

    return true;
  };

  // Get minimum price error message
  const getMinimumPriceError = (language = "en") => {
    // Check if only utility items (no services)
    if (selectedUtilityItems.length > 0 && selectedServices.length === 0) {
      return language === "ar"
        ? "يجب اختيار خدمة واحدة على الأقل مع المستلزمات"
        : "You must select at least one service with utility items";
    }

    if (selectedServices.length > 0 && minimumPrice > 0) {
      const servicesPrice = selectedServices.reduce(
        (sum, service) => sum + service.price * (service.quantity || 1),
        0
      );

      if (servicesPrice < minimumPrice) {
        return language === "ar"
          ? `الحد الأدنى للخدمات ${minimumPrice} ر.س. المبلغ الحالي ${servicesPrice} ر.س (المستلزمات لا تحتسب)`
          : `Minimum services amount is ${minimumPrice} SAR. Current amount is ${servicesPrice} SAR (utility items not included)`;
      }
    }
    return null;
  };

  // Calculate total price including discount and quantities
  const calculateTotalPrice = () => {
    const servicesPrice = selectedServices.reduce(
      (sum, service) => sum + service.price * (service.quantity || 1),
      0
    );
    const utilityPrice = selectedUtilityItems.reduce(
      (sum, item) => sum + item.price * (item.quantity || 1),
      0
    );
    const packagesPrice = packages.reduce((sum, pkg) => sum + pkg.price, 0);
    const subtotal = servicesPrice + utilityPrice + packagesPrice;

    // Apply discount if promo code exists
    let discountAmount = 0;
    if (promoCode) {
      discountAmount = subtotal * (promoCode.discount / 100);

      if (promoCode.maxDiscount && discountAmount > promoCode.maxDiscount) {
        discountAmount = promoCode.maxDiscount;
      }
    }

    return {
      servicesPrice,
      utilityPrice,
      packagesPrice,
      discount: discountAmount,
      totalPrice: subtotal - discountAmount,
      subtotal,
    };
  };

  // Get all items in cart (services + utility items + packages)
  const getAllItems = () => {
    return [...selectedServices, ...selectedUtilityItems, ...packages];
  };

  // Get total items count (including quantities)
  const getTotalItemsCount = () => {
    const servicesCount = selectedServices.reduce(
      (sum, service) => sum + (service.quantity || 1),
      0
    );
    const utilityCount = selectedUtilityItems.reduce(
      (sum, item) => sum + (item.quantity || 1),
      0
    );
    const packagesCount = packages.length;
    return servicesCount + utilityCount + packagesCount;
  };

  // Get unique items count (not including quantities)
  const getUniqueItemsCount = () => {
    return selectedServices.length + selectedUtilityItems.length + packages.length;
  };

  // Update the current user
  const updateCurrentUser = (user) => {
    setCurrentUser(user);
  };

  // Context value
  const value = {
    selectedServices,
    selectedUtilityItems,
    packages,
    appointmentDetails,
    selectedAddress,
    promoCode,
    notes,
    minimumPrice,
    loading,
    addService,
    addServices,
    addUtilityItem,
    addPackage,
    removeItem,
    clearCart,
    clearServices,
    clearUtilityItems,
    setAppointment,
    updateSelectedAddress,
    applyPromoCode,
    removePromoCode,
    updateNotes,
    updateMinimumPrice,
    calculateTotalPrice,
    getAllItems,
    updateCurrentUser,
    updateServiceQuantity,
    updateUtilityQuantity,
    getTotalItemsCount,
    getUniqueItemsCount,
    isPaymentAllowed,
    getMinimumPriceError,
    hasServices: selectedServices.length > 0,
    hasUtilityItems: selectedUtilityItems.length > 0,
    hasPackages: packages.length > 0,
    hasItems: selectedServices.length > 0 || selectedUtilityItems.length > 0 || packages.length > 0,
    hasPromoCode: promoCode !== null,
    hasMinimumPrice: minimumPrice > 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartProvider;